import { IonIcon } from '@ionic/react';
import {
  cameraOutline,
  flashOutline,
  layersOutline,
  briefcaseOutline,
  shieldCheckmarkOutline,
  megaphoneOutline,
  trophyOutline,
} from 'ionicons/icons';
import type { CandidateBadge, CandidateBadgeCode } from '@/types';

/**
 * Candidate badge shelf — shows all seven possible badges.
 * Earned badges (Active docs) render coloured; locked badges are greyed
 * with a "How to earn" hint. Server awards only — no client mutation.
 *
 * @see Documentation/Specification/020_Definitions/application-specific/workseeking/CandidateBadge.md
 * @see sprints/2026-10/Milestone_09_BluePagesCandidateGamification/sprint-03-candidate-badges
 */
interface BadgeMeta {
  label: string;
  hint: string;
  icon: string;
}

export const BADGE_META: Record<CandidateBadgeCode, BadgeMeta> = {
  PhotoAdded: { label: 'Photo Added', hint: 'Add a profile photo', icon: cameraOutline },
  FirstSkill: { label: 'First Skill', hint: 'Add your first skill', icon: flashOutline },
  SkillBuilder: { label: 'Skill Builder', hint: 'List 5 or more skills', icon: layersOutline },
  ExperienceAdded: { label: 'Experience Added', hint: 'Add your first work experience', icon: briefcaseOutline },
  IdentityVerified: { label: 'Identity Verified', hint: 'Verify your identity', icon: shieldCheckmarkOutline },
  OpenToWork: { label: 'Open to Work', hint: 'Be marketplace visible with Immediate availability', icon: megaphoneOutline },
  ProfileComplete: { label: 'Profile Complete', hint: 'Reach the Gold tier (score 90+)', icon: trophyOutline },
};

export const ALL_BADGE_CODES = Object.keys(BADGE_META) as CandidateBadgeCode[];

interface BadgeShelfProps {
  badges?: CandidateBadge[] | null;
}

export function BadgeShelf({ badges }: BadgeShelfProps) {
  const earned = new Set(
    (badges ?? []).filter((b) => b.status === 'Active').map((b) => b.badgeCode),
  );

  return (
    <div className="bp-card" data-testid="badge-shelf" style={{ padding: '14px 16px' }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#f1f5f9', marginBottom: 10 }}>
        Badges
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
          gap: 8,
        }}
      >
        {ALL_BADGE_CODES.map((code) => {
          const meta = BADGE_META[code];
          const isEarned = earned.has(code);
          return (
            <div
              key={code}
              data-testid={`badge-${code}`}
              data-earned={isEarned}
              title={isEarned ? meta.label : `Locked — ${meta.hint}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                padding: '10px 6px',
                borderRadius: 12,
                background: isEarned ? 'rgba(56,189,248,0.12)' : 'rgba(255,255,255,0.04)',
                border: isEarned ? '1px solid rgba(56,189,248,0.45)' : '1px solid rgba(255,255,255,0.08)',
                opacity: isEarned ? 1 : 0.55,
                textAlign: 'center',
              }}
            >
              <IonIcon
                icon={meta.icon}
                style={{
                  fontSize: '1.4rem',
                  color: isEarned ? 'var(--ion-color-primary, #38bdf8)' : 'var(--bp-text-muted, #64748b)',
                }}
              />
              <span
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: isEarned ? '#e2e8f0' : 'var(--bp-text-muted, #64748b)',
                }}
              >
                {meta.label}
              </span>
              {!isEarned && (
                <span style={{ fontSize: '0.6rem', color: 'var(--bp-text-muted, #64748b)' }}>
                  Locked
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
